import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Send, MessageCircle, Paperclip, X, Image as ImageIcon, Video, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MessagesPage({ user }) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [adminId, setAdminId] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    fetchMessages();
    // Poll for new messages every 10 seconds
    const interval = setInterval(fetchMessages, 10000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = async () => {
    const token = localStorage.getItem('token');
    try {
      // First get conversations to find admin ID
      const convRes = await fetch(`${API}/messages/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (convRes.ok) {
        const conversations = await convRes.json();
        if (conversations.length > 0) {
          const admin = conversations[0];
          setAdminId(admin.user_id);

          // Fetch messages
          const msgRes = await fetch(`${API}/messages/${admin.user_id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });

          if (msgRes.ok) {
            setMessages(await msgRes.json());
          }
        }
      }
    } catch (error) {
      console.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if ((!newMessage.trim() && !selectedFile) || !adminId) return;

    setSending(true);
    const token = localStorage.getItem('token');
    let fileData = null;

    try {
      // Upload file first if selected
      if (selectedFile) {
        const formData = new FormData();
        formData.append('file', selectedFile);

        const uploadResponse = await fetch(`${API}/messages/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (!uploadResponse.ok) {
          throw new Error('Failed to upload file');
        }

        fileData = await uploadResponse.json();
      }

      // Send message with or without file
      const response = await fetch(`${API}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          receiver_id: adminId,
          content: newMessage.trim() || '📎 File attachment',
          file_url: fileData ? fileData.filename : null,
          file_type: fileData ? fileData.file_type : null,
          file_name: fileData ? fileData.original_name : null
        })
      });

      if (response.ok) {
        setNewMessage('');
        setSelectedFile(null);
        setFilePreview(null);
        fetchMessages();
        toast.success('Message sent');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (50MB max)
    if (file.size > 50 * 1024 * 1024) {
      toast.error('File too large. Maximum 50MB allowed');
      return;
    }

    setSelectedFile(file);

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else if (file.type.startsWith('video/')) {
      setFilePreview('video');
    } else {
      setFilePreview('document');
    }
  };

  const clearFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const renderMessageContent = (msg) => {
    return (
      <>
        {msg.content && <p className="whitespace-pre-wrap mb-2">{msg.content}</p>}
        
        {msg.file_url && (
          <div className="mt-2">
            {msg.file_type === 'image' && (
              <img
                src={`${API}/messages/media/${msg.file_url}`}
                alt={msg.file_name || 'Image'}
                className="max-w-full rounded-lg max-h-64 object-cover cursor-pointer"
                onClick={() => window.open(`${API}/messages/media/${msg.file_url}`, '_blank')}
              />
            )}
            
            {msg.file_type === 'video' && (
              <video
                controls
                className="max-w-full rounded-lg max-h-64"
                src={`${API}/messages/media/${msg.file_url}`}
              >
                Your browser does not support video playback.
              </video>
            )}
            
            {msg.file_type === 'document' && (
              <a
                href={`${API}/messages/media/${msg.file_url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 p-3 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
              >
                <FileText className="w-5 h-5" />
                <span className="text-sm">{msg.file_name || 'Download file'}</span>
              </a>
            )}
          </div>
        )}
        
        <p className="text-xs mt-2 opacity-60">
          {new Date(msg.created_at).toLocaleString()}
        </p>
      </>
    );
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#F4EFE6]">
      <nav className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4">
        <div className="container mx-auto flex justify-between items-center">
          <Button 
            data-testid="back-btn" 
            onClick={() => navigate('/dashboard')} 
            variant="ghost" 
            className="rounded-full px-2 sm:px-4"
          >
            <ArrowLeft className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Back to Dashboard</span><span className="sm:hidden">Back</span>
          </Button>
          <div className="text-lg sm:text-2xl font-bold text-[#0B0B0B]" style={{ fontFamily: 'Clash Display' }}>
            Messages
          </div>
          <div></div>
        </div>
      </nav>

      <div className="container mx-auto px-4 sm:px-6 py-4 sm:py-12" data-testid="messages-page">
        <Card className="bg-white rounded-2xl overflow-hidden" style={{ height: 'calc(100vh - 180px)' }}>
          <div className="bg-[#0B0B0B] text-white px-6 py-4 flex items-center gap-3">
            <MessageCircle className="w-6 h-6" />
            <div>
              <div className="font-bold" style={{ fontFamily: 'Clash Display' }}>MoveMore Support</div>
              <div className="text-xs text-gray-300">Your personal rehabilitation coach</div>
            </div>
          </div>

          <div className="flex flex-col h-full">
            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <p className="text-gray-500">Loading messages...</p>
              </div>
            ) : messages.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center p-12">
                <MessageCircle className="w-16 h-16 text-gray-300 mb-4" />
                <h3 className="text-xl font-bold text-[#0B0B0B] mb-2">No messages yet</h3>
                <p className="text-gray-600 text-center max-w-md mb-6">
                  Start a conversation with your MoveMore coach! Ask questions about your programs or get personalized advice.
                </p>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto p-6 space-y-4" style={{ maxHeight: 'calc(100vh - 450px)' }}>
                {messages.map((msg, index) => {
                  const isFromMe = msg.sender_id === user.id;
                  return (
                    <motion.div
                      key={msg.id || index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className={`flex ${isFromMe ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[70%] rounded-2xl px-4 py-3 ${
                          isFromMe
                            ? 'bg-[#D9C7A3] text-[#0B0B0B]'
                            : 'bg-gray-100 text-[#0B0B0B]'
                        }`}
                      >
                        {renderMessageContent(msg)}
                      </div>
                    </motion.div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            )}

            <form onSubmit={handleSendMessage} className="border-t border-gray-200 p-4">
              {selectedFile && (
                <div className="mb-3 p-3 bg-gray-100 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {filePreview === 'video' && <Video className="w-5 h-5 text-gray-600" />}
                    {filePreview === 'document' && <FileText className="w-5 h-5 text-gray-600" />}
                    {filePreview && filePreview !== 'video' && filePreview !== 'document' && (
                      <img src={filePreview} alt="Preview" className="w-12 h-12 object-cover rounded" />
                    )}
                    <div>
                      <p className="text-sm font-medium text-gray-700">{selectedFile.name}</p>
                      <p className="text-xs text-gray-500">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    onClick={clearFile}
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
              
              <div className="flex gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileSelect}
                  accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx"
                  className="hidden"
                />
                <Button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  variant="outline"
                  className="rounded-full px-4"
                  disabled={sending}
                  data-testid="attach-file-btn"
                >
                  <Paperclip className="w-4 h-4" />
                </Button>
                <Textarea
                  data-testid="message-input"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type your message..."
                  className="flex-1 resize-none"
                  rows={2}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage(e);
                    }
                  }}
                />
                <Button
                  type="submit"
                  data-testid="send-message-btn"
                  disabled={sending || (!newMessage.trim() && !selectedFile)}
                  className="bg-[#D9C7A3] text-[#0B0B0B] hover:bg-[#C6A97C] rounded-full px-6"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Press Enter to send, Shift+Enter for new line • Max 50MB per file
              </p>
            </form>
          </div>
        </Card>
      </div>
    </div>
  );
}
